import classnames from 'classnames';
import React from 'react';

import { ElsButton } from '@scopus/experimental-components-wrappers-react';

import { useTableCellAction } from '../../context/TableCellContext';
import CellAction, { Fields } from './CellAction';
import { useEventSystem } from '../../components/EventProvider/EventProvider';
import prettifyValue from '../../utils/prettifyValue';
import { ViewRequest } from '../../graphql/generated/graphql';
import { Action } from '../types';

import styles from './table.module.scss';
import sanitizeHTML from '../../utils/sanitizeHTML';

interface CellActions {
  actions?: Action[];
  fields: Fields;
}
export interface Props {
  linkTo?: string | null;
  value: string;
  type: string;
  entityRequest?: ViewRequest;
  cellActions?: CellActions;
  clickable?: boolean;
  handleClick?: () => void;
  link?: string;
  externalLink?: string | undefined;
}

const CellValue = ({
  clickable,
  link,
  externalLink,
  prettyValue,
  shouldLineThroughTheValues,
  handleClick,
}: {
  prettyValue: string;
  shouldLineThroughTheValues: boolean;
  clickable?: boolean;
  link?: string;
  externalLink?: string;
  handleClick: () => void;
}): JSX.Element => {
  const CellTag = shouldLineThroughTheValues ? 'del' : 'span';

  if (externalLink) {
    return (
      <ElsButton
        className={classnames({
          [styles.lineThrough]: shouldLineThroughTheValues,
        })}
        external
        screenReaderText="Opens in a new tab"
        size="m"
        variant="link-alt"
        href={externalLink}
      >
        {prettyValue}
      </ElsButton>
    );
  }
  if (link) {
    return (
      <a
        className={classnames({
          [styles.lineThrough]: shouldLineThroughTheValues,
        })}
        href={link}
        onClick={handleClick}
      >
        <CellTag className="link__text text-bold" dangerouslySetInnerHTML={{ __html: prettyValue }} />
      </a>
    );
  }
  if (clickable) {
    return (
      <button className="button--link-black" onClick={handleClick} type="button">
        <CellTag
          className={classnames(`button__text text-bold text--alight-left ${styles.clamp} ${styles.outlinePadding}`, {
            [styles.lineThrough]: shouldLineThroughTheValues,
          })}
          dangerouslySetInnerHTML={{ __html: prettyValue }}
        />
      </button>
    );
  }

  return <CellTag className={classnames(styles.clamp)} dangerouslySetInnerHTML={{ __html: prettyValue }} />;
};

const Cell = React.memo(({ type, link, linkTo, entityRequest, value, externalLink, clickable, cellActions }: Props) => {
  const { dispatchEvent } = useEventSystem();
  const handleClick = React.useCallback(() => {
    const fields = {
      ...cellActions?.fields,
      type: linkTo,
      entityRequest,
    };
    cellActions?.fields && dispatchEvent('clickRow', fields);
  }, [cellActions?.fields, dispatchEvent, entityRequest, linkTo]);

  const prettyValue = sanitizeHTML(prettifyValue(value, type));
  const { acknowledgedValues } = useTableCellAction();
  const removedGrantIds = acknowledgedValues ? acknowledgedValues['removeGrant'] : [];
  const shouldLineThroughTheValues =
    removedGrantIds && removedGrantIds.length > 0 && removedGrantIds.includes(cellActions?.fields.entityId);

  return (
    <td className={classnames(styles.cellText, { [styles.currencyValue]: type === 'CURRENCY' })}>
      <CellValue
        prettyValue={prettyValue}
        handleClick={handleClick}
        shouldLineThroughTheValues={shouldLineThroughTheValues}
        link={link}
        externalLink={externalLink}
        clickable={clickable}
      />
      {cellActions?.actions &&
        cellActions.actions.map((action: Action) => (
          <CellAction key={action.actionName} action={action} fields={cellActions.fields} />
        ))}
    </td>
  );
});

Cell.displayName = 'Cell';

export default Cell;
